import { Injectable, signal } from "@angular/core";
import { Capacitor } from "@capacitor/core";

import { I18nService } from "@bitwarden/common/abstractions/i18n.service";
import { LogService } from "@bitwarden/common/abstractions/log.service";
import { AbstractStorageService } from "@bitwarden/common/abstractions/storage.service";
import { HtmlStorageLocation } from "@bitwarden/common/enums/htmlStorageLocation";
import {
  cancelLocalNotifications,
  checkLocalNotificationPermission,
  requestLocalNotificationPermission,
  scheduleLocalNotification,
} from "@bitwarden/web-vault/app/capacitor-plugins/local-notifications";

export type NotificationPermissionState = "granted" | "denied" | "prompt" | "unavailable";

export type NotifyDays = 1 | 3 | 7;

export type PaymentType =
  | "bill"
  | "subscription"
  | "loan"
  | "insurance"
  | "utilities"
  | "rent"
  | "creditCard"
  | "other";

export interface BillAlert {
  id: number;
  paymentType: PaymentType;
  payee: string;
  dueDate: string;
  amount?: number;
  notifyDaysBefore: NotifyDays[];
}

const BILL_ALERTS_STORAGE_KEY = "gloss_bill_alerts";
const BILL_ALERTS_NEXT_ID_KEY = "gloss_bill_alerts_next_id";

@Injectable({ providedIn: "root" })
export class PushNotificationService {
  readonly isMobile = Capacitor.isNativePlatform();

  readonly permissionState = signal<NotificationPermissionState>("prompt");
  readonly billAlerts = signal<BillAlert[]>([]);
  readonly isLoading = signal(true);

  private nextId = 1;

  constructor(
    private logService: LogService,
    private i18nService: I18nService,
    private storageService: AbstractStorageService,
  ) {}

  async initialize() {
    const stored = await this.storageService.get<BillAlert[]>(BILL_ALERTS_STORAGE_KEY, {
      htmlStorageLocation: HtmlStorageLocation.Local,
    });
    const existing = stored ?? [];
    this.billAlerts.set(existing);
    const storedNextId = await this.storageService.get<number>(BILL_ALERTS_NEXT_ID_KEY, {
      htmlStorageLocation: HtmlStorageLocation.Local,
    });
    const derivedNextId = existing.length > 0 ? Math.max(...existing.map((a) => a.id)) + 1 : 1;
    this.nextId = Math.max(storedNextId ?? 1, derivedNextId);

    if (!this.isMobile) {
      this.permissionState.set("unavailable");
      this.isLoading.set(false);
      return;
    }
    await this.refreshPermissionState();
    this.isLoading.set(false);
  }

  async requestPermission(): Promise<boolean> {
    if (!this.isMobile) {
      return false;
    }
    const result = await requestLocalNotificationPermission();
    const granted = result.display === "granted";
    this.permissionState.set(granted ? "granted" : "denied");
    if (granted) {
      await this.rescheduleAllAlerts();
    }
    return granted;
  }

  async refreshPermissionState() {
    const result = await checkLocalNotificationPermission();
    if (result.display === "granted") {
      this.permissionState.set("granted");
    } else if (result.display === "denied") {
      this.permissionState.set("denied");
    } else {
      this.permissionState.set("prompt");
    }
  }

  async saveBillAlert(alert: Omit<BillAlert, "id">): Promise<BillAlert> {
    const id = this.nextId++;
    const newAlert: BillAlert = { ...alert, id };
    const updated = [...this.billAlerts(), newAlert];
    this.billAlerts.set(updated);
    await this.persistBillAlerts(updated);
    await this.persistNextId();
    if (this.permissionState() === "granted") {
      await this.scheduleAlertNotification(newAlert);
    }
    return newAlert;
  }

  async deleteBillAlert(id: number) {
    const alert = this.billAlerts().find((a) => a.id === id);
    if (!alert) {
      return;
    }
    const updated = this.billAlerts().filter((a) => a.id !== id);
    this.billAlerts.set(updated);
    await this.persistBillAlerts(updated);
    const notifIds = alert.notifyDaysBefore.map((d) => alert.id * 100 + d);
    await cancelLocalNotifications(notifIds);
  }

  private async scheduleAlertNotification(alert: BillAlert) {
    const [year, month, day] = alert.dueDate.split("T")[0].split("-").map(Number);
    const dueDate = new Date(year, month - 1, day);
    for (const days of alert.notifyDaysBefore) {
      const notifyDate = new Date(dueDate);
      notifyDate.setDate(notifyDate.getDate() - days);
      notifyDate.setHours(9, 0, 0, 0);
      if (notifyDate <= new Date()) {
        continue;
      }
      try {
        await scheduleLocalNotification({
          id: alert.id * 100 + days,
          title: this.i18nService.t("billDueNotificationTitle", alert.payee),
          body: this.buildNotificationBody(alert, days),
          scheduleAt: notifyDate,
        });
      } catch (e) {
        this.logService.error("[PushNotificationService] Failed to schedule notification: " + e);
      }
    }
  }

  private buildNotificationBody(alert: BillAlert, days: number): string {
    const daysLabel = this.getNotifyDaysLabel(days);
    if (alert.amount != null) {
      return days === 1
        ? this.i18nService.t("billDueNotificationBodySingular", alert.payee, String(alert.amount))
        : this.i18nService.t(
            "billDueNotificationBody",
            alert.payee,
            String(alert.amount),
            daysLabel,
          );
    }
    return days === 1
      ? this.i18nService.t("billDueNotificationBodySingularNoAmount", alert.payee)
      : this.i18nService.t("billDueNotificationBodyNoAmount", alert.payee, daysLabel);
  }

  getNotifyDaysLabel(days: number): string {
    if (days === 1) {
      return this.i18nService.t("notifyOneDay");
    }
    if (days === 3) {
      return this.i18nService.t("notifyThreeDays");
    }
    if (days === 7) {
      return this.i18nService.t("notifyOneWeek");
    }
    return `${days} days`;
  }

  private async rescheduleAllAlerts() {
    for (const alert of this.billAlerts()) {
      await this.scheduleAlertNotification(alert);
    }
  }

  private async persistNextId() {
    try {
      await this.storageService.save(BILL_ALERTS_NEXT_ID_KEY, this.nextId, {
        htmlStorageLocation: HtmlStorageLocation.Local,
      });
    } catch (e) {
      this.logService.error("[PushNotificationService] Failed to persist nextId: " + e);
    }
  }

  private async persistBillAlerts(alerts: BillAlert[]) {
    try {
      await this.storageService.save(BILL_ALERTS_STORAGE_KEY, alerts, {
        htmlStorageLocation: HtmlStorageLocation.Local,
      });
    } catch (e) {
      this.logService.error("[PushNotificationService] Failed to persist alerts: " + e);
    }
  }
}
